/**
 * Shared with me — Wave 2 finish (b51).
 *
 * Lists every document that the current user has access to via an
 * EXPLICIT ACL grant (direct user / group / role on the doc itself, or
 * via a folder grant). The tenant-wide-fallback set is excluded — this
 * is "what was deliberately shared with me", not "everything I can see".
 *
 * Backed by GET /api/v1/library/acl/shared-with-me — and by nothing else.
 * The page is the server's answer (LB6-T4):
 *
 *   - WHO SHARED each document arrives with the row as `granter`
 *     (decision 30, B9). The page does not look anyone up — no roster, no
 *     picker, no second query. `granter: null` is a system grant ("System"),
 *     or — with `grantedBy` set — a sharer outside this tenant, whom the
 *     server does not name.
 *   - The title search is a parameter of the request: the server searches
 *     the whole list before paging, so a match on page 2 is found and the
 *     count is the matches'. The browser filters nothing.
 *   - A failed read is an error, with no count and no empty-state copy.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Loader2, Share2, FileText, Eye, Search } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { apiClient } from '@/lib/api-client';
import type { NamedUser } from '@argusai/shared/schemas/user-display';
import {
  SHARED_WITH_ME_SEARCH_MAX,
  type SharedWithMeResponse,
  type SharedWithMeRow,
} from '@argusai/shared/schemas/library-sharing';

export const Route = createFileRoute('/library/shared')({
  component: SharedWithMePage,
});

// ─── Types: THE schema the server answers with (LB6-T4-R3) ────────────────
//
// No hand-written mirror: the rows are typed from `SharedWithMeResponseSchema`
// in @argusai/shared, the schema GET /library/acl/shared-with-me validates its
// response with. A new `via` kind or granter field is a compile error here.

/** Who shared it, named by the server (the picker projection). */
export type SharedGranter = NamedUser;
export type SharedRow = SharedWithMeRow;
export type SharedResponse = SharedWithMeResponse;
type AclPermission = SharedRow['permission'];

/** How long the search box waits for typing to settle before it asks. */
const SEARCH_SETTLE_MS = 300;

/**
 * The text a settle should apply, or `null` when there is nothing to settle:
 * the trimmed input is what is already searched (a trailing space, the first
 * render). Only a real change resets the reader to page 1 (LB6-T4-R4/BF-04).
 */
export function searchToSettle(searchInput: string, search: string): string | null {
  const next = searchInput.trim();
  return next === search ? null : next;
}

/** The last page of a list of `total` rows; 1 for an empty list. */
function lastPageOf(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

/**
 * The page to show for the server's `total`: a page past the end (the list
 * shrank under the reader — shares expired or were revoked) becomes the last
 * page, never an empty page with no way back (LB6-T4-BF-02).
 */
export function clampPage(page: number, total: number, pageSize: number): number {
  return Math.min(Math.max(1, page), lastPageOf(total, pageSize));
}

/** The pager shows when there is more than one page, or the reader is past page 1. */
export function showPager(totalPages: number, page: number): boolean {
  return totalPages > 1 || page > 1;
}

/**
 * THE page's one read: the key and the request, together. `search` is the
 * settled text; empty means no search parameter.
 */
export function sharedWithMeQuery(args: { page: number; pageSize: number; search: string }) {
  const params = new URLSearchParams({ page: String(args.page), pageSize: String(args.pageSize) });
  if (args.search) params.set('search', args.search);
  return {
    queryKey: ['library', 'shared-with-me', { page: args.page, pageSize: args.pageSize, search: args.search }] as const,
    queryFn: () => apiClient.get<SharedResponse>(`/library/acl/shared-with-me?${params.toString()}`),
  };
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function viaLabel(via: SharedRow['via']): string {
  switch (via) {
    case 'doc_user':     return 'Direct (user)';
    case 'doc_group':    return 'Group';
    case 'doc_role':     return 'Role';
    case 'doc_department': return 'Department';
    case 'folder_user':  return 'Folder (user)';
    case 'folder_group': return 'Folder (group)';
    case 'folder_role':  return 'Folder (role)';
    case 'folder_department': return 'Folder (department)';
  }
}

/**
 * The "Shared by" cell: the server's name; "System" for a system grant (no
 * `grantedBy`); and for a sharer the server does not name — an account that
 * was never of this tenant (LB6-T4-R2) — "Outside this tenant", never
 * "System" and never "Unknown".
 */
export function granterLabel(granter: SharedGranter | null, grantedBy: string | null = null): string {
  if (granter) return granter.name;
  return grantedBy ? 'Outside this tenant' : 'System';
}

/** Its tooltip: the email, and the id for an audit trace. */
function granterTitle(granter: SharedGranter | null, grantedBy: string | null): string {
  if (granter) return `${granter.name} <${granter.email}> — id: ${granter.id}`;
  return grantedBy ? `Shared by an account that is not a member of this tenant — id: ${grantedBy}` : 'System grant';
}

function permVariant(p: AclPermission): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (p === 'share' || p === 'delete') return 'destructive';
  if (p === 'edit') return 'default';
  return 'secondary';
}

// ─── Page ─────────────────────────────────────────────────────────────────

export function SharedWithMePage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  // Settle the search only when the trimmed text really changed: no timer on
  // the first render or for a trailing space, so a reader on page 3 stays on
  // page 3 (LB6-T4-R4/BF-04).
  useEffect(() => {
    const next = searchToSettle(searchInput, search);
    if (next === null) return;
    const settle = setTimeout(() => {
      setSearch(next);
      setPage(1);
    }, SEARCH_SETTLE_MS);
    return () => clearTimeout(settle);
  }, [searchInput, search]);

  // The ONE read. The rows, the granter names and the count are its answer.
  const sharedQuery = useQuery(sharedWithMeQuery({ page, pageSize, search }));

  const rows = sharedQuery.data?.rows ?? [];
  const total = sharedQuery.data?.total;
  const totalPages = lastPageOf(total ?? 0, pageSize);

  // The list shrank under a later page: move to its last page (LB6-T4-BF-02).
  useEffect(() => {
    if (total === undefined) return;
    const clamped = clampPage(page, total, pageSize);
    if (clamped !== page) setPage(clamped);
  }, [total, page, pageSize]);

  return (
    <div className="p-6 space-y-4">
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Share2 className="h-4 w-4" /> Shared with me
        </h2>
        <p className="text-sm text-muted-foreground">
          Documents and folders that have been explicitly shared with you.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search by title or filename..."
            className="pl-8 h-8 text-sm"
            // The server's bound (LB6-T4-BF-03): a longer paste is cut here, never a 400.
            maxLength={SHARED_WITH_ME_SEARCH_MAX}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        {/* A count is an answer: none is shown until the server gave one. */}
        {sharedQuery.isSuccess && total !== undefined && (
          <span className="text-xs text-muted-foreground">
            {total} document{total === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {sharedQuery.isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading shared documents...
        </div>
      )}

      {sharedQuery.isError && (
        <div className="text-sm text-destructive py-8 text-center">
          Failed to load shared documents. Try again.
        </div>
      )}

      {/* Empty means the server counted none — an empty page of a non-empty
          list is a page past the end, and the clamp above moves off it. */}
      {sharedQuery.isSuccess && rows.length === 0 && total === 0 && (
        <div className="py-12 text-center">
          <FileText className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            {search
              ? 'No matching documents.'
              : 'Nothing has been explicitly shared with you yet.'}
          </p>
        </div>
      )}

      {sharedQuery.isSuccess && rows.length > 0 && (
        // overflow-x-auto so the rightmost "Open" action stays reachable on
        // tablet (≤768px) where the grid would otherwise clip its tail.
        <div className="border rounded-md overflow-x-auto">
          <div className="grid grid-cols-[minmax(180px,1fr)_120px_140px_180px_180px_80px] gap-3 px-4 py-2 border-b bg-muted/30 text-[11px] font-medium uppercase tracking-wider text-muted-foreground min-w-[820px]">
            <div>Document</div>
            <div>Permission</div>
            <div>Via</div>
            <div>Shared by</div>
            <div>Granted at</div>
            <div className="text-right">Open</div>
          </div>
          <div>
            {rows.map((r) => (
              <div
                key={r.documentId + ':' + r.permission}
                className="grid grid-cols-[minmax(180px,1fr)_120px_140px_180px_180px_80px] gap-3 items-center px-4 py-2.5 border-b last:border-b-0 text-sm hover:bg-muted/20 min-w-[820px]"
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">{r.title}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {r.fileName}
                    {r.expiresAt && ` · expires ${formatDate(r.expiresAt)}`}
                  </div>
                </div>
                <div>
                  <Badge variant={permVariant(r.permission)} className="text-[10px]">
                    {r.permission}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground">{viaLabel(r.via)}</div>
                <div className="text-xs truncate" title={granterTitle(r.granter, r.grantedBy)}>
                  {granterLabel(r.granter, r.grantedBy)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {formatDate(r.grantedAt)}
                </div>
                <div className="text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    title="Open in Library"
                    onClick={() => navigate({ to: `/library/doc/${r.documentId}` })}
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showPager(totalPages, page) && (
        <div className="flex justify-between items-center text-xs text-muted-foreground pt-2">
          <div>
            Page {page} of {totalPages}
          </div>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
